import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  VStack,
  Text,
  type ModalProps,
} from '@chakra-ui/react';
import { useEffect, type FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Icon } from '$/components/common/Icon';
import { ModalHeaderWithBackButton } from '$/components/common/Modals/ModalHeaderWithBackButton';
import { useAuthenticationStore } from '$/components/core/Authentication/stores/useAuthenticationStore';
import { ConfirmationForm } from '$/components/core/Profile/DeleteAccountConfirmationForm';
import { StyledLink } from '$/components/core/StyledLink';
import { trackContentImpression, trackContentInteraction } from '$/logger';

export const DeleteAccountModal: FC<Omit<ModalProps, 'children'>> = (props) => {
  const { t } = useTranslation();

  const { user } = useAuthenticationStore.getState();

  useEffect(() => {
    if (props.isOpen) {
      trackContentImpression('CloudAccount', 'DeleteAccount', 'Open');
    }
  }, [props.isOpen]);

  const onClose = () => {
    trackContentInteraction(
      'ClickButton',
      'CloudAccount',
      'ManageAccount',
      'Close',
    );

    props.onClose();
  };

  return (
    <Modal {...props} isCentered onClose={onClose}>
      <ModalOverlay />
      <ModalContent maxW='500px' bg='background'>
        <ModalHeaderWithBackButton
          headerText={t('dashboard.authentication.deleteAccount.action')}
          trackingContentName='CloudAccount'
          trackingContentPiece={'DeleteAccount'}
          trackingContentTarget='Close'
        />
        <ModalCloseButton top='0' right='0' mt='10' mr='10' />
        <ModalBody p='10'>
          <VStack
            alignItems='flex-start'
            gap='2.5rem'
            overflowY='auto'
            maxH='100%'
          >
            <Icon icon='sad_emoji' w='80px' h='80px' />
            <Text fontSize='sm'>
              <Trans
                i18nKey='dashboard.authentication.deleteAccount.instruction'
                values={{
                  email: user?.email,
                  confirmation: t(
                    'dashboard.authentication.deleteAccount.confirmation',
                  ),
                }}
                shouldUnescape
                components={{
                  PrivacyLink: (
                    <StyledLink
                      color='primaryButton.background'
                      textDecoration='underline'
                      to='/privacy'
                    >
                      Link
                    </StyledLink>
                  ),
                }}
              />
            </Text>
            <ConfirmationForm onClose={props.onClose} />
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
